import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, GitCompare, Loader2, MoreHorizontal, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/i18n";
import {
  deleteStandardAgreement,
  downloadStandardAgreement,
  MAX_STANDARD_AGREEMENT_BYTES,
  StandardAgreement,
  STANDARD_AGREEMENT_ACCEPT,
  updateStandardAgreement,
  uploadStandardAgreement,
} from "../../services/StandardAgreements";
import { COMPARABLE_FILE, isFailed, isReady, nameFromFileName } from "./compare-utils";
import { RetryButton, StandardChip } from "./StandardStatus";

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface Details {
  name: string;
  agreementType: string;
  description: string;
}
const NO_DETAILS: Details = { name: "", agreementType: "", description: "" };

interface Props {
  standards: StandardAgreement[];
  reload: () => Promise<void>;
  /** Select this standard on the Compare tab */
  onCompare: (id: string) => void;
}

/**
 * The organization's standard agreements: add, rename, download, delete, retry.
 * The Compare tab's first step picks from the same list.
 */
const StandardsLibrary: React.FC<Props> = ({ standards, reload, onCompare }) => {
  const { t, locale } = useI18n();
  const c = t.comparisons;
  const s = c.lib;
  const fileInput = useRef<HTMLInputElement>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [details, setDetails] = useState<Details>(NO_DETAILS);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<StandardAgreement | null>(null);
  const [deleting, setDeleting] = useState<StandardAgreement | null>(null);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale === "sv" ? "sv-SE" : "en-GB", { year: "numeric", month: "short", day: "numeric" });

  const openUpload = () => {
    setFile(null);
    setDetails(NO_DETAILS);
    setUploadOpen(true);
  };

  const pickFile = (picked: File | undefined) => {
    if (!picked) return;
    if (!COMPARABLE_FILE.test(picked.name)) return void toast.error(s.unsupportedFile);
    if (picked.size > MAX_STANDARD_AGREEMENT_BYTES) return void toast.error(s.tooLarge);
    setFile(picked);
    setDetails((d) => ({ ...d, name: d.name || nameFromFileName(picked.name) }));
  };

  const submitUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadStandardAgreement(file, details);
      if (res.success && res.data) {
        toast.success(s.uploaded(res.data.name));
        setUploadOpen(false);
        await reload();
      } else {
        toast.error(res.status === 413 ? s.tooLarge : s.uploadFailed);
      }
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (item: StandardAgreement) => {
    setDetails({ name: item.name, agreementType: item.agreementType ?? "", description: item.description ?? "" });
    setEditing(item);
  };

  const submitEdit = async () => {
    if (!editing || !details.name.trim()) return;
    setBusy(true);
    try {
      const res = await updateStandardAgreement(editing.id, {
        name: details.name.trim(),
        agreementType: details.agreementType.trim() || null,
        description: details.description.trim() || null,
      });
      if (res.success) {
        toast.success(s.saved);
        setEditing(null);
        await reload();
      } else {
        toast.error(s.saveFailed);
      }
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      const res = await deleteStandardAgreement(deleting.id);
      if (res.success) {
        toast.success(s.deleted);
        setDeleting(null);
        await reload();
      } else {
        toast.error(s.deleteFailed);
      }
    } finally {
      setBusy(false);
    }
  };

  const download = async (item: StandardAgreement) => {
    const res = await downloadStandardAgreement(item.id);
    if (!res.success || !res.data) return void toast.error(s.downloadFailed);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const detailFields = (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sa-name">{s.name}</Label>
        <Input id="sa-name" value={details.name} maxLength={200} placeholder={s.namePlaceholder} onChange={(e) => setDetails({ ...details, name: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sa-type">{s.agreementType}</Label>
        <Input id="sa-type" value={details.agreementType} maxLength={100} placeholder={s.typePlaceholder} onChange={(e) => setDetails({ ...details, agreementType: e.target.value })} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="sa-description">{s.descriptionLabel}</Label>
        <Textarea id="sa-description" value={details.description} maxLength={2000} rows={3} placeholder={s.descriptionPlaceholder} onChange={(e) => setDetails({ ...details, description: e.target.value })} />
      </div>
    </div>
  );

  return (
    <section aria-labelledby="standards-library" className="space-y-4" data-testid="standards-library">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-64">
          <h2 id="standards-library" className="text-xl font-semibold">
            {s.title}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="standards-intro">
            {s.intro}
          </p>
        </div>
        <Button onClick={openUpload} data-testid="library-upload">
          <Upload className="mr-2 h-4 w-4" />
          {s.upload}
        </Button>
      </div>

      {standards.length === 0 ? (
        <button
          type="button"
          onClick={openUpload}
          className="flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center transition-colors hover:bg-muted/40"
        >
          <FileText className="h-8 w-8 text-muted-foreground" />
          <div className="font-medium">{s.empty}</div>
          <div className="max-w-md text-sm text-muted-foreground">{s.emptyHint}</div>
        </button>
      ) : (
        <Card className="divide-y overflow-hidden">
          {standards.map((item) => (
            <div key={item.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="standard-row">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-words font-medium">{item.name}</span>
                  <StandardChip standard={item} />
                  {isFailed(item) && <RetryButton standard={item} onDone={reload} />}
                </div>
                {(item.agreementType || item.description) && (
                  <div className="line-clamp-2 text-sm text-muted-foreground">
                    {[item.agreementType, item.description].filter(Boolean).join(" · ")}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <FileText className="h-3 w-3 shrink-0" />
                    <span className="truncate" title={item.fileName}>
                      {item.fileName}
                    </span>
                    <span className="shrink-0">· {formatSize(item.sizeBytes)}</span>
                  </span>
                  {item.uploadedBy && (
                    <span>
                      {s.addedBy} {item.uploadedBy}
                    </span>
                  )}
                  <span>{formatDate(item.createdAt)}</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="outline" size="sm" disabled={!isReady(item)} onClick={() => onCompare(item.id)} data-testid="library-compare">
                  <GitCompare className="mr-1.5 h-3.5 w-3.5" />
                  {c.compare}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0" aria-label={s.moreActions}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => void download(item)}>
                      <Download className="mr-2 h-4 w-4" />
                      {s.download}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEdit(item)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      {s.edit}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setDeleting(item)} className="text-red-600">
                      <Trash2 className="mr-2 h-4 w-4" />
                      {s.delete}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={uploadOpen} onOpenChange={(open) => !busy && setUploadOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{s.uploadDialogTitle}</DialogTitle>
            <DialogDescription>{s.uploadDialogDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
              className={`flex w-full flex-col items-center gap-1 rounded-lg border border-dashed px-4 py-6 text-center transition-colors ${
                dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40"
              }`}
            >
              {file ? (
                <>
                  <FileText className="h-6 w-6 text-primary" />
                  <div className="max-w-full truncate text-sm font-medium">{file.name}</div>
                  <div className="text-xs text-muted-foreground">{formatSize(file.size)}</div>
                </>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <div className="text-sm font-medium">{s.chooseFile}</div>
                  <div className="text-xs text-muted-foreground">{s.fileTypes}</div>
                </>
              )}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept={STANDARD_AGREEMENT_ACCEPT}
              className="hidden"
              data-testid="library-file-input"
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            {detailFields}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setUploadOpen(false)}>
              {s.cancel}
            </Button>
            <Button disabled={!file || busy} onClick={() => void submitUpload()}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              {busy ? s.uploading : s.upload}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing != null} onOpenChange={(open) => !open && !busy && setEditing(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{s.editDialogTitle}</DialogTitle>
            <DialogDescription>{editing?.fileName}</DialogDescription>
          </DialogHeader>
          {detailFields}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setEditing(null)}>
              {s.cancel}
            </Button>
            <Button disabled={!details.name.trim() || busy} onClick={() => void submitEdit()}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {s.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleting != null} onOpenChange={(open) => !open && !busy && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{s.deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{deleting ? s.deleteDescription(deleting.name) : null}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{s.cancel}</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {s.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default StandardsLibrary;
